import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { OrderOptions } from '@/shared/lib';
import {
  formatInstantToPtBR,
  parseDate,
  useContentLoadingState,
  useCustomToast,
  useInfiniteScrollObserver,
  useListUrlSync,
} from '@/shared/lib';
import type {
  OperationLogDocumentTypeFilter,
  OperationLogEventFilter,
  OperationLogFilters,
  OperationLogListParams,
  OperationLogResponse,
} from '@/entities/OperationLog';
import {
  OPERATION_LOG_DOCUMENT_TYPE_FILTER_OPTIONS,
  OPERATION_LOG_EVENT_FILTER_OPTIONS,
  operationLogApi,
} from '@/entities/OperationLog';
import { membersApi } from '@/entities/Branch';
import { positionApi } from '@/entities/Position';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import { useTableFilterModal } from '@/shared/lib/use';
import OperationLogsFilterModal from '../ui/OperationLogsFilterModal.vue';

interface SelectorOption {
  id: number;
  name: string;
}

interface RoleOption {
  id: string;
  name: string;
}

const ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Ação mais recente', sort: 'occurredAt', order: 'desc' },
  { label: 'Ação mais antiga', sort: 'occurredAt', order: 'asc' },
];

const DEFAULT_ORDER = 'desc' as const;

const DEFAULT_FILTERS: OperationLogFilters = {
  startDate: null,
  endDate: null,
  userId: null,
  positionName: null,
  documentType: null,
  event: null,
};

const SEARCH_FIELD_MAP: Record<string, 'ip_address' | 'description'> = {
  ipAddress: 'ip_address',
  description: 'description',
};

const INFINITE_SCROLL_PAGE_SIZE = 30;

const LOG_QUANTITY_OPTIONS = [
  { id: 10, name: '10 por página' },
  { id: 25, name: '25 por página' },
  { id: 50, name: '50 por página' },
  { id: SHOW_ALL_ITEMS_QUANTITY, name: 'Todos os logs' },
];

const VALID_DOCUMENT_TYPES = OPERATION_LOG_DOCUMENT_TYPE_FILTER_OPTIONS.map(
  (option) => option.value
);
const VALID_EVENTS = OPERATION_LOG_EVENT_FILTER_OPTIONS.map((option) => option.value);

const ERROR_MESSAGE =
  'Não foi possível carregar os logs de operações agora. Tente novamente em instantes.';

const getQueryString = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : null;
  }
  return typeof value === 'string' ? value : null;
};

const parseIdFromQuery = (value: unknown): number | null => {
  const raw = getQueryString(value);
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export function useOperationLogsList() {
  const route = useRoute();

  const readFiltersFromUrl = (): OperationLogFilters => {
    const documentTypeRaw = getQueryString(route.query.documentType);
    const eventRaw = getQueryString(route.query.event);

    return {
      startDate: parseDate(getQueryString(route.query.startDate)),
      endDate: parseDate(getQueryString(route.query.endDate)),
      userId: parseIdFromQuery(route.query.userId),
      positionName: getQueryString(route.query.positionName),
      documentType: VALID_DOCUMENT_TYPES.includes(documentTypeRaw as OperationLogDocumentTypeFilter)
        ? (documentTypeRaw as OperationLogDocumentTypeFilter)
        : null,
      event: VALID_EVENTS.includes(eventRaw as OperationLogEventFilter)
        ? (eventRaw as OperationLogEventFilter)
        : null,
    };
  };

  const { searchTerm, searchFieldId, sort, order, currentPage, syncUrl } = useListUrlSync({
    fields: Object.keys(SEARCH_FIELD_MAP),
    defaultField: 'ipAddress',
    validSorts: ['occurredAt'],
    neutralOrder: 'desc',
  });
  const selectedQuantity = ref(10);
  const filters = ref<OperationLogFilters>(readFiltersFromUrl());
  const logs = ref<OperationLogResponse[]>([]);
  const allItems = ref<OperationLogResponse[]>([]);
  const totalLogs = ref(0);
  const totalPages = ref(1);
  const loading = ref(false);
  const loadingMore = ref(false);
  const loadMoreFailed = ref(false);
  const hasMoreItems = ref(true);
  const error = ref('');
  const collaboratorOptions = ref<SelectorOption[]>([]);
  const roleOptions = ref<RoleOption[]>([]);

  const { showErrorToast } = useCustomToast();

  const isInfiniteScrollMode = computed(
    () => Number(selectedQuantity.value) === SHOW_ALL_ITEMS_QUANTITY
  );

  const hasRenderedLogs = computed(() => logs.value.length > 0);
  const { showSkeleton, isRefreshing } = useContentLoadingState(loading, hasRenderedLogs);

  const formatDate = (value: string) => formatInstantToPtBR(value, { withTime: false });
  const formatTime = (value: string) => {
    const formatted = formatInstantToPtBR(value);

    if (formatted === '-') {
      return '-';
    }

    return formatted.split(' | ')[1] ?? '-';
  };

  const normalizeFilters = (value: Partial<OperationLogFilters> = {}): OperationLogFilters => ({
    ...DEFAULT_FILTERS,
    ...value,
  });

  const getDateKey = (value: Date | null) => value?.toISOString() ?? null;

  const areFiltersEqual = (left: OperationLogFilters, right: OperationLogFilters) =>
    getDateKey(left.startDate) === getDateKey(right.startDate) &&
    getDateKey(left.endDate) === getDateKey(right.endDate) &&
    left.userId === right.userId &&
    left.positionName === right.positionName &&
    left.documentType === right.documentType &&
    left.event === right.event;

  const buildParams = (page: number): OperationLogListParams => {
    const trimmedSearch = searchTerm.value.trim();

    return {
      page,
      limit: isInfiniteScrollMode.value
        ? INFINITE_SCROLL_PAGE_SIZE
        : Number(selectedQuantity.value),
      sort: 'occurredAt',
      // `||` (not `??`) is intentional: an empty string means "no explicit order", which must fall back to the default.
      order: order.value || DEFAULT_ORDER,
      ...(trimmedSearch
        ? {
            search: trimmedSearch,
            searchField: SEARCH_FIELD_MAP[searchFieldId.value] ?? 'ip_address',
          }
        : {}),
      startDate: filters.value.startDate,
      endDate: filters.value.endDate,
      userId: filters.value.userId,
      positionName: filters.value.positionName,
      documentType: filters.value.documentType,
      event: filters.value.event,
    };
  };

  const buildExportParams = (): OperationLogListParams => buildParams(1);

  let requestSeq = 0;

  const resetPaginationState = () => {
    currentPage.value = 1;
    hasMoreItems.value = true;
    loadMoreFailed.value = false;
    loadingMore.value = false;
  };

  const fetchPage = async (page: number) => {
    const seq = ++requestSeq;
    loading.value = true;
    error.value = '';

    try {
      const response = await operationLogApi.getOperationLogs(buildParams(page));
      if (seq !== requestSeq) return;

      logs.value = response.data;
      totalLogs.value = response.metadata.total;
      totalPages.value = response.metadata.totalPages;
      currentPage.value = page;

      if (isInfiniteScrollMode.value) {
        allItems.value = response.data;
        hasMoreItems.value = page < response.metadata.totalPages;
      }
    } catch {
      if (seq !== requestSeq) return;

      logs.value = [];
      allItems.value = [];
      totalLogs.value = 0;
      totalPages.value = 1;
      hasMoreItems.value = false;
      error.value = ERROR_MESSAGE;
    } finally {
      if (seq === requestSeq) loading.value = false;
    }
  };

  const loadMore = async () => {
    if (!isInfiniteScrollMode.value || loading.value || loadingMore.value || !hasMoreItems.value) {
      return;
    }

    if (loadMoreFailed.value) {
      return;
    }

    const seq = ++requestSeq;
    loadingMore.value = true;

    const nextPage = currentPage.value + 1;

    try {
      const response = await operationLogApi.getOperationLogs(buildParams(nextPage));
      if (seq !== requestSeq) return;

      allItems.value = [...allItems.value, ...response.data];
      logs.value = allItems.value;
      totalLogs.value = response.metadata.total;
      totalPages.value = response.metadata.totalPages;
      currentPage.value = nextPage;
      hasMoreItems.value = nextPage < response.metadata.totalPages;
    } catch {
      if (seq !== requestSeq) return;

      loadMoreFailed.value = true;
      showErrorToast({ title: ERROR_MESSAGE, type: 'error' });
    } finally {
      if (seq === requestSeq) loadingMore.value = false;
    }
  };

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMore,
  });

  const buildFilterQuery = () => ({
    startDate: filters.value.startDate,
    endDate: filters.value.endDate,
    userId: filters.value.userId,
    positionName: filters.value.positionName,
    documentType: filters.value.documentType,
    event: filters.value.event,
  });

  const syncUrlWithFilters = () => syncUrl(buildFilterQuery());

  const handleSearch = (value: string) => {
    searchTerm.value = value;
    resetPaginationState();
    syncUrlWithFilters();
    void fetchPage(1);
  };

  const handleSortUpdate = (sortField: string, emittedOrder: string) => {
    order.value = emittedOrder === 'asc' || emittedOrder === 'desc' ? emittedOrder : '';
    sort.value = order.value ? sortField : '';
    resetPaginationState();
    syncUrlWithFilters();
    void fetchPage(1);
  };

  const handleFilterUpdate = (value: OperationLogFilters) => {
    const nextFilters = normalizeFilters(value);

    if (areFiltersEqual(filters.value, nextFilters)) {
      return;
    }

    filters.value = nextFilters;
    resetPaginationState();
    syncUrlWithFilters();
    void fetchPage(1);
  };

  const handlePageChanged = (page: number) => {
    currentPage.value = page;
    syncUrlWithFilters();
    void fetchPage(page);
  };

  const fetchAllPositions = async () => {
    const FILTER_OPTIONS_PAGE_SIZE = 1000;
    const positions: SelectorOption[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await positionApi.getAllPositions({
        page,
        itemsPerPage: FILTER_OPTIONS_PAGE_SIZE,
      });
      positions.push(...response.data);
      totalPages = response.metadata?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages);

    return positions;
  };

  const loadFilterOptions = async () => {
    try {
      const [members, positions] = await Promise.all([
        membersApi.getAllMembersPaginated(100, undefined, undefined, { includeInactiveMembers: true }),
        fetchAllPositions(),
      ]);

      collaboratorOptions.value = members
        .filter((member) => member.user?.id && member.user.name)
        .map((member) => ({
          id: member.user?.id ?? 0,
          name: member.user?.name ?? '',
        }))
        .filter((option) => option.id > 0 && option.name.trim() !== '');

      const uniqueRoleNames = new Set<string>();
      roleOptions.value = positions
        .filter((position) => position.name.trim() !== '')
        .filter((position) => {
          const name = position.name.trim();
          if (uniqueRoleNames.has(name)) {
            return false;
          }
          uniqueRoleNames.add(name);
          return true;
        })
        .map((position) => ({
          id: position.name,
          name: position.name,
        }));
    } catch {
      collaboratorOptions.value = [];
      roleOptions.value = [];
    }
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    OperationLogsFilterModal,
    { clearFilters: () => undefined, filters: filters.value },
    handleFilterUpdate,
    () => filters.value,
    () => ({
      closeOnClear: false,
      onClearFilters: () => undefined,
      collaboratorOptions: collaboratorOptions.value,
      roleOptions: roleOptions.value,
    })
  );

  const resetForRouteLeave = () => {
    filters.value = { ...DEFAULT_FILTERS };
    sort.value = '';
    order.value = '';
    searchTerm.value = '';
    currentPage.value = 1;
  };

  onMounted(() => {
    void loadFilterOptions();
    void fetchPage(currentPage.value);
  });

  watch(selectedQuantity, () => {
    const nextPageSize = isInfiniteScrollMode.value
      ? INFINITE_SCROLL_PAGE_SIZE
      : Number(selectedQuantity.value);
    const visibleLogs = logs.value.slice(0, nextPageSize);

    logs.value = visibleLogs;
    allItems.value = isInfiniteScrollMode.value ? [...visibleLogs] : [];
    resetPaginationState();
    syncUrlWithFilters();
    void fetchPage(1);
  });

  return {
    searchTerm,
    searchFieldId,
    sort,
    order,
    currentPage,
    selectedQuantity,
    filters,
    logs,
    allItems,
    totalLogs,
    totalPages,
    loading,
    showSkeleton,
    isRefreshing,
    loadingMore,
    hasMoreItems,
    error,
    isFilterActive,
    openFilterModal,
    handleSearch,
    handleSortUpdate,
    handlePageChanged,
    buildExportParams,
    formatDate,
    formatTime,
    resetForRouteLeave,
    ORDER_OPTIONS,
    LOG_QUANTITY_OPTIONS,
  };
}
